/* eslint-disable react/jsx-no-undef */
import React from 'react';
import {
  Button,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import {
  faEnvelope,
  faPenToSquare,
  faStickyNote,
  faCalendarCheck,
  faCreditCard,
  faBolt,
  faUniversity,
  faEllipsisVertical,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

import Arta from '@artaio/arta-browser';
import { imagePath, cfPath, awsServer, isProd } from '../../../utilities/UrlPaths';
import { formatDate } from '../../../utilities/invoiceUtils';
import { SHIPPING_METHODS_MAP } from '../constants';
import { STAGE_ARTA_API_KEY, PROD_ARTA_API_KEY } from '../../../config';

Arta.init(isProd ? PROD_ARTA_API_KEY : STAGE_ARTA_API_KEY);

const formatChannel = (channelText) => {
  let abbreviation = 'INV';
  if (channelText.trim().indexOf('Infinite Bidding') > -1 || channelText.trim().indexOf('Connect') > -1) {
    abbreviation = 'PL';
  } else if (channelText.trim() === 'AuctionZip') {
    abbreviation = 'AZ';
  } else if (channelText.trim().indexOf('Floor') > -1) {
    abbreviation = 'Floor';
  }

  return abbreviation;
};

const SinglePackage = ({ pack, showIcon = false }) => {
  // Arta Tracking Widget
  const openTrackingModal = (e) => {
    e.preventDefault();
    const artaShipID = pack?.artaShipmentID;
    const config = {
      animation: {
        in: {
          type: 'slide',
          duration: 400,
          easing: 'ease-in-out',
        },
        out: {
          type: 'slide',
          duration: 250,
          easing: 'ease-in-out',
        },
      },
      style: {
        color: {
          iconPrimary: 'black',
        },
      },
    };
    const tracking = Arta.tracking(artaShipID, config);
    tracking.validate()
      .then(() => {
        if (tracking.isReady) tracking.open();
      })
      .catch(error => {
        console.error('Error validating tracking shipment:', error);
      });
  };

  /*
  Tracking Status:"pending" "confirmed" "collected" "in_transit" "completed" "cancelled"
  */
  const getStatusPill = (shippingStatus) => {
    switch (shippingStatus) {
      case 'pending':
        return <span className="status-pill status-pill-pending">pending</span>;
      case 'in_transit':
        return <span className="status-pill">in transit</span>;
      case 'completed':
        return <span className="status-pill">completed<FontAwesomeIcon icon={faCheck} className="ml-1 check-icon"/></span>;
      case 'cancelled':
        return <span className="status-pill status-pill-canceled">canceled</span>;
      case 'confirmed':
        return <span className="status-pill">confirmed</span>;
      case 'collected':
        return <span className="status-pill">collected</span>;
      default:
        return <span>{shippingStatus}</span>;
    }
  };
  if (pack?.shippingMethod === 'invaluable_shipping') {
    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        {showIcon &&
          <img src={`${awsServer}/static/invShippingIcon.png`} alt="shipping truck" className="shippingTruckIcon height-20" />}
        <p className="mb-0 text-nowrap">Inv Ship</p>
        {pack?.artaShipmentID && <>
          <Button className="invoice-button" onClick={openTrackingModal}>
            <img src={`${awsServer}/static/link.svg`} alt="shipment tracking link" className="shipment-tracking-link-icon" />
          </Button>
          {getStatusPill(pack?.shipmentStatus)}
        </>}
      </div>
    );
  } else {
    return (
      <p className="mb-0">{SHIPPING_METHODS_MAP[pack?.shippingMethod]}</p>
    );
  }
};

const MoreMenuButton = ({ invoiceID, moreMenuId, setMoreMenuId }) => (
  <div className="d-flex mx-2 justify-content-between">
    <Button
      className={`invoice-button ${moreMenuId === invoiceID ? 'active' : ''}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setMoreMenuId(moreMenuId === invoiceID ? null : invoiceID);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          setMoreMenuId(moreMenuId === invoiceID ? null : invoiceID);
        }
      }}
      aria-label={`More menu for shipping status for invoice ${invoiceID}`}
      aria-expanded={moreMenuId === invoiceID}
      aria-haspopup="true"
      tabIndex={0}
    >
      <Tooltip title="More Menu" placement="right">
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </Tooltip>
    </Button>
  </div>
);

const InvoiceGroupTableCols = (
  invoiceGroupID,
  exportFunctions,
  openCalendar,
  isSingleRow,
  paymentProcessingEnabled,
  setMoreMenuId,
  moreMenuId,
  moreMenuRef
) => {
  const tableColumns = [
    {
      Header: (
        <span className="ml-4 pl-2">
          <Input
            type="checkbox"
            onChange={exportFunctions.handleOnAllChangeInvoicesChecked}
            checked={exportFunctions.isEveryInvoicesChecked}
          />
        </span>
      ),
      headerClassName: 'd-flex align-items-center',
      id: 'checkbox',
      className: 'wrap-table-cell',
      minWidth: 35,
      sortable: false,
      accessor: d => d,
      Cell: ({ value: { invoiceID } }) => {
        if (!invoiceID) {
          return null;
        }
        return (
          <span className="ml-4 pl-1">
            <Input
              type="checkbox"
              onChange={exportFunctions.handleOnChangeInvoiceChecked(
                invoiceID
              )}
              checked={exportFunctions.checkedInvoices[invoiceID]}
            />
          </span>
        );
      }
    },
    {
      Header: ('Actions'),
      headerClassName: 'd-flex align-items-center',
      id: 'actions',
      className: 'wrap-table-cell rt-table-cell-height',
      minWidth: 78,
      sortable: false,
      accessor: d => d,
      Cell: ({ value: { invoiceID, hasNote } }) => {
        if (!invoiceID) {
          return null;
        }
        return (
          <span>
            <Button
              color="link"
              className="invoice-action-button"
              id={`edit_${invoiceID}`}
              href={`${imagePath}/invoices/EditInvoices/${invoiceGroupID}?id=${invoiceID}`}
            >
              <FontAwesomeIcon icon={faPenToSquare} />
              <UncontrolledTooltip placement={isSingleRow ? 'right' : 'top'} target={`edit_${invoiceID}`} className="table-tooltip">
                Edit Invoice
              </UncontrolledTooltip>
            </Button>
            <Button
              color="link"
              className="invoice-action-button"
              id={`contact_${invoiceID}`}
              onClick={() => {
                exportFunctions.setIsContactModalOpen(true);
                exportFunctions.setCurrentInvoiceID(invoiceID);
              }}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              <UncontrolledTooltip placement={isSingleRow ? 'right' : 'top'} target={`contact_${invoiceID}`} className="table-tooltip">
                Contact
              </UncontrolledTooltip>
            </Button>
            <Button
              color="link"
              className="invoice-action-button"
              id={`note_${invoiceID}`}
              onClick={() => {
                exportFunctions.setCurrentInvoiceID(invoiceID);
                exportFunctions.setIsAddNoteModalOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faStickyNote} className={hasNote ? 'has-note' : ''} id={`noteIcon_${invoiceID}`} />
              <UncontrolledTooltip placement={isSingleRow ? 'right' : 'top'} target={`note_${invoiceID}`} className="table-tooltip">
                {hasNote ? 'Update Note' : 'Add Note'}
              </UncontrolledTooltip>
            </Button>
          </span>
        );
      }
    },
    {
      Header: (<div>Invoice # <FontAwesomeIcon className="ml-1 not-small" icon="sort" /></div>),
      headerClassName: 'd-flex align-items-center',
      id: 'invoice',
      className: 'wrap-table-cell',
      minWidth: 85,
      sortable: true,
      accessor: d => d,
      sortMethod: (a, b) => {
        const aValue = (!Number.isNaN(Number(a.invoiceID))) ? Number(a.invoiceID) : 0;
        const bValue = (!Number.isNaN(Number(b.invoiceID))) ? Number(b.invoiceID) : 0;
        if (aValue === bValue) return 0;
        return (aValue > bValue) ? 1 : -1;
      },
      Cell: ({ value: { invoiceID, hasRestrictedItems, batchEligible, batchFailReason, autoChargeDate, isPaid } }) => {
        if (!invoiceID) {
          return null;
        }

        return (
          <span>
            <Link
              onClick={exportFunctions.handleClickInvoiceLink}
              to={`/invoices/InvoiceGroupPreview/${invoiceGroupID}/invoice/${invoiceID}`}
            >
              {invoiceID}
            </Link>
            { hasRestrictedItems && paymentProcessingEnabled && ('*') }
            {batchEligible === 1 && (
              <span className=" ml-1 cursor-pointer" id={`id_${invoiceID}`}>
                <FontAwesomeIcon icon={faBolt} className="bolt-green" />
              </span>
            )}
            {batchEligible === 0 && (
              <span className=" ml-1 cursor-pointer" id={`id_${invoiceID}`}>
                <FontAwesomeIcon icon={faBolt} className="ml-1 bolt-gray" />
              </span>
            )}
            <UncontrolledTooltip placement="right" target={`id_${invoiceID}`} className="other-tooltip">
              {batchFailReason}
            </UncontrolledTooltip>

            {autoChargeDate > 0 && !isPaid && (
              <>
                <span className=" ml-1 cursor-pointer" id={`autocharge_${invoiceID}`}>
                  <FontAwesomeIcon icon={faCalendarCheck} className="ml-1 bolt-green" />
                </span>
                <UncontrolledTooltip placement="right" target={`autocharge_${invoiceID}`} className="table-tooltip">
                  Auto-Charge {formatDate(new Date(autoChargeDate))}
                </UncontrolledTooltip>
              </>
            )}
          </span>
        );
      }
    }
  ];
  const paddleColumn = {
    Header: (<div>Paddle <FontAwesomeIcon className="ml-1 not-small" icon="sort" /></div>),
    headerClassName: 'd-flex align-items-center',
    id: 'paddle',
    isVisible: false,
    className: 'wrap-table-cell',
    minWidth: 62,
    sortable: true,
    accessor: d => d,
    sortMethod: (a, b) => {
      const aValue = (!Number.isNaN(Number(a.paddleNumber))) ? Number(a.paddleNumber) : 0;
      const bValue = (!Number.isNaN(Number(b.paddleNumber))) ? Number(b.paddleNumber) : 0;
      if (aValue === bValue) return 0;
      return (aValue > bValue) ? 1 : -1;
    },
    Cell: ({ value: { paddleNumber } }) => {
      if (!paddleNumber) {
        return null;
      }

      return <span>{paddleNumber}</span>;
    }
  };
  const tableColumns2 = [
    {
      Header: (<div>Name <FontAwesomeIcon className="ml-1 not-small" icon="sort" /></div>),
      headerClassName: 'd-flex align-items-center',
      id: 'lastname',
      className: 'wrap-table-cell',
      minWidth: 185,
      sortable: true,
      accessor: d => d,
      sortMethod: (a, b) => {
        const aValue = (a.lastName.length) ? a.lastName.toLowerCase() : '';
        const bValue = (b.lastName.length) ? b.lastName.toLowerCase() : '';
        if (aValue === bValue) return 0;
        return (aValue > bValue) ? 1 : -1;
      },
      Cell: ({ value: { lastName, firstName, approvalID, channel } }) => {
        if (!lastName) {
          return null;
        }

        let displayName = `${lastName}, ${firstName}`;
        const hoverName = displayName;
        let showTooltip = false;
        if (displayName.length > 25) {
          showTooltip = true;
          displayName = `${displayName.substring(0, 22)}...`;
        }

        return (
          <>
            <div id={`hover_${approvalID}`}>
              <a href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${approvalID}`} target="_blank" rel="noopener noreferrer">
                {displayName}
              </a>
              <span> | {formatChannel(channel)}</span>
            </div>
            {showTooltip && (
            <UncontrolledTooltip placement="right" target={`hover_${approvalID}`} className="name-tooltip other-tooltip">
              {hoverName}
            </UncontrolledTooltip>
            )}
          </>
        );
      }
    },
    {
      Header: (<div>Invoice Total<FontAwesomeIcon className="ml-1 not-small" icon="sort" /></div>),
      headerClassName: 'd-flex align-items-center',
      id: 'total',
      className: 'wrap-table-cell',
      minWidth: 100,
      sortable: true,
      accessor: d => d,
      sortMethod: (rowA, rowB) => {
        const a = parseFloat(rowA.totalString.replaceAll(',', ''));
        const b = parseFloat(rowB.totalString.replaceAll(',', ''));
        return a > b ? 1 : -1;
      },
      Cell: ({ value: { totalString, currencySymbol } }) => {
        if (!totalString) {
          return null;
        }

        return (

          <div>
            <div className="float-left">
              {currencySymbol}
            </div>
            <div className="float-right mr-2">
              { totalString }
            </div>
          </div>
        );
      }
    },
    {
      Header: (<div>Balance<FontAwesomeIcon className="ml-1 not-small" icon="sort" /></div>),
      headerClassName: 'd-flex align-items-center',
      id: 'outstanding',
      className: 'wrap-table-cell',
      minWidth: 90,
      sortable: true,
      accessor: d => d,
      sortMethod: (rowA, rowB) => {
        const a = parseFloat(rowA.balanceString.replaceAll(',', ''));
        const b = parseFloat(rowB.balanceString.replaceAll(',', ''));
        return a > b ? 1 : -1;
      },
      Cell: ({ value: { balanceString, currencySymbol } }) => {
        if (!balanceString) {
          return null;
        }

        return (

          <div>
            <div className="float-left">
              {currencySymbol}
            </div>
            <div className="float-right mr-2">
              { balanceString }
            </div>
          </div>
        );
      }
    },
    {
      Header: (<div>Invoice Status<FontAwesomeIcon className="ml-1 not-small" icon="sort" /></div>),
      headerClassName: 'd-flex align-items-center',
      id: 'paid-status',
      className: 'wrap-table-cell',
      minWidth: 180,
      sortable: true,
      accessor: d => d,
      sortMethod: (a, b) => {
        const aValue = (a.paidStatus.length) ? a.paidStatus.toLowerCase() : '';
        const bValue = (b.paidStatus.length) ? b.paidStatus.toLowerCase() : '';
        if (aValue === bValue) return 0;
        return (aValue > bValue) ? 1 : -1;
      },
      Cell: ({ value: { paidStatus, invoiceID, isPaid, paidByPaymentProcessing, paymentMethodText, isPending, paidTimestamp, paymentMethodID, isPickup } }) => (
        <div>
          <div className={`float-left ${paymentMethodText && 'cursor-pointer'}`} id={`status_${invoiceID}`}>
            {paidStatus} <span className="font-90">{paidTimestamp}</span>
            {isPaid && paymentMethodText && (
              <UncontrolledTooltip placement={isSingleRow ? 'right' : 'top'} target={`status_${invoiceID}`} className="table-tooltip">
                Paid by {paymentMethodText}
              </UncontrolledTooltip>
            )}
          </div>
          {isPaid && paidByPaymentProcessing && (
            <div className="float-right mr-2" id={`icon_${invoiceID}`}>
              {paymentMethodID === 11 ? (
                <>
                  <FontAwesomeIcon icon={faUniversity} className="fa-lg" />
                  <UncontrolledTooltip placement="right" target={`icon_${invoiceID}`} className="table-tooltip">
                    Paid via ACH
                  </UncontrolledTooltip>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCreditCard} className="fa-lg" />
                  <UncontrolledTooltip placement="right" target={`icon_${invoiceID}`} className="table-tooltip">
                    Paid via CC
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          )}
          {(!paidByPaymentProcessing || (!isPaid && !isPending)) && (
            <div className="float-right">
              <Button
                color="link"
                className="invoice-action-button"
                id={`paid_${invoiceID}`}
                onClick={openCalendar}
                invoiceid={invoiceID}
                pickup={isPickup.toString()}
                detailid={0}
                variant="Paid"
                currentstatus={isPaid.toString()}
              >
                <FontAwesomeIcon icon={faPenToSquare} />
                <UncontrolledTooltip placement={isSingleRow ? 'right' : 'top'} target={`paid_${invoiceID}`} className="table-tooltip">
                  Edit Status
                </UncontrolledTooltip>
              </Button>
            </div>
          )}
          <div className="clear-both" />
        </div>
      ),
    },
    {
      Header: (<div>Ship/Pickup Status<FontAwesomeIcon className="ml-1 not-small" icon="sort" /></div>),
      headerClassName: 'd-flex align-items-center',
      id: 'ship-status',
      className: 'wrap-table-cell',
      minWidth: 160,
      sortable: true,
      accessor: d => d,
      sortMethod: (a, b) => {
        const aValue = (a.shipStatus.length) ? a.shipStatus.toLowerCase() : '';
        const bValue = (b.shipStatus.length) ? b.shipStatus.toLowerCase() : '';
        if (aValue === bValue) return 0;
        return (aValue > bValue) ? 1 : -1;
      },
      Cell: ({ value: { shipStatus, invoiceID, isShipped, isPickup, useLegacyPathForShipStatus } }) => {
        if (useLegacyPathForShipStatus || typeof shipStatus === 'string' && shipStatus !== 'Loading...') {
          return (
            <div>
              <div className="float-left">
                {typeof shipStatus === 'string' ? shipStatus : SHIPPING_METHODS_MAP[shipStatus?.overallStatus]}
              </div>
              <div className="float-right">
                <Button
                  color="link"
                  className="invoice-action-button"
                  id={`ship_${invoiceID}`}
                  onClick={(e) => openCalendar(e)}
                  invoiceid={invoiceID}
                  detailid={0}
                  variant="Shipped"
                  currentstatus={isShipped.toString()}
                  pickup={isPickup.toString()}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                  <UncontrolledTooltip placement={isSingleRow ? 'right' : 'top'} target={`ship_${invoiceID}`} className="table-tooltip">
                    Edit Status
                  </UncontrolledTooltip>
                </Button>
              </div>
              <div className="clear-both" />
            </div>
          );
        } else {
          return (
            <div className="d-flex">
              {(shipStatus === 'Loading...') ?
                <span>{shipStatus}</span>
                :
                <div className="d-flex w-100 justify-content-between position-relative">
                  {shipStatus?.shipments?.size === 1 && <SinglePackage pack={shipStatus.shipments.values().next().value} showIcon/>}
                  {shipStatus?.shipments?.size > 1 &&
                    <>
                      <span>{shipStatus?.overallStatus}</span>
                      <MoreMenuButton invoiceID={invoiceID} moreMenuId={moreMenuId} setMoreMenuId={setMoreMenuId} />
                    </>
                  }
                  {moreMenuId === invoiceID && (
                    <div
                      role="menu"
                      aria-label={`Multiple packages shipping status details for invoice ${invoiceID}`}
                      className="more-menu"
                      ref={(el) => {
                        if (el) moreMenuRef.current[invoiceID] = el;
                        else delete moreMenuRef.current[invoiceID];
                      }}
                    >
                      {Array.from(shipStatus.shipments.keys()).map((key, index) => {
                        return (
                          <div key={`package-${index}`} className="mb-2">
                          <p className="mb-0 font-12">Package {index + 1}</p>
                          <SinglePackage pack={shipStatus.shipments.get(key)}/>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              }
            </div>
          );
        }
      }
    }
  ];

  //  conditional columns
  if (exportFunctions.hasPaddles) tableColumns.push(paddleColumn);
  return [...tableColumns, ...tableColumns2];
};

export default InvoiceGroupTableCols;
